import React, { useState, useEffect } from 'react';
import './Hero.css';
import { toSentenceCase } from '../utils/textUtils';

// Vector SVG Icons
const IconEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconDownload = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconShieldCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const IconExternalLink = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const IconPrinter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const IconTrophy = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.45 1-1 1H7v4h10v-4h-2c-.55 0-1-.45-1-1v-2.34" />
    <path d="M6 4h12v6a6 6 0 0 1-12 0V4z" />
  </svg>
);

const IconAwardMedal = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

const IconCrown = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" />
  </svg>
);

const IconSparkle = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"/>
  </svg>
);

function Hero() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  
  const images = [
    '/images/etudiante.jpg',
    '/images/batiment-ecole.jpg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change d'image toutes les 5 secondes

    return () => clearInterval(interval);
  }, [images.length]);

  const handleNavigation = (page) => {
    if (window.navigateTo) {
      if (page === 'inscription') {
        window.navigateTo('student-registration');
      } else {
        window.navigateTo(page);
      }
      window.scrollTo(0, 0);
    }
  };

  const handleDownloadBrochure = (brochureUrl, brochureName) => {
    const link = document.createElement('a');
    link.href = brochureUrl;
    link.target = '_blank';
    link.download = brochureName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const polesData = [
    {
      code: 'CPEG',
      name: 'Collège Général',
      desc: 'Formation générale solide avec un accompagnement personnalisé de la 6ème à la 3ème.',
      iconClass: 'cpeg',
      pdfUrl: '/documents/brochures/brochure_cpeg.pdf',
      pdfFilename: 'Brochure_CPEG_Les_Palmiers.pdf',
      iconSvg: (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      code: 'CPET',
      name: 'Collège & Lycée Technique',
      desc: 'Apprentissage technique de pointe (F3, F4, G2, HR, DWM, MMV, IMI, MA) avec ateliers modernes.',
      iconClass: 'cpet',
      pdfUrl: '/documents/brochures/brochure_cpet.pdf',
      pdfFilename: 'Flyers_Technique_Les_Palmiers.pdf',
      iconSvg: (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      code: 'CPFPA',
      name: 'Formation Professionnelle',
      desc: 'Formations modulaires certifiantes et pratiques pour une insertion professionnelle rapide.',
      iconClass: 'cpfpa',
      pdfUrl: '/documents/brochures/brochure_cpfpa.pdf',
      pdfFilename: 'Brochure_CPFPA_Les_Palmiers.pdf',
      iconSvg: (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      code: 'ISP',
      name: 'Institut Supérieur Polytechnique',
      desc: 'Enseignement supérieur technique de haut niveau, licences et diplômes professionnels d\'État.',
      iconClass: 'isp',
      pdfUrl: '/documents/brochures/brochure_isp.pdf',
      pdfFilename: 'Brochure_ISP_Les_Palmiers.pdf',
      iconSvg: (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  const performanceAchievements = [
    {
      rank: "1",
      rankSuffix: "er",
      rankSub: "Choix d'État",
      badgeText: "Reconnaissance Officielle",
      type: "gold",
      title: "1er Lycée d'Abomey-Calavi",
      desc: "1er lycée de la commune d'Abomey-Calavi choisi par l'État béninois après le lycée d'Akassato.",
      tag: "Commune d'Abomey-Calavi"
    },
    {
      rank: "1",
      rankSuffix: "er",
      rankSub: "Major National",
      badgeText: "BAC G2 2024 • N°1 National",
      type: "gold",
      title: "1er du Bénin & 7ème National",
      desc: "1er du Bénin au BAC G2 2024 et 7ème du Bénin au Baccalauréat toutes séries confondues.",
      tag: "Excellence Académique"
    },
    {
      rank: "2",
      rankSuffix: "ème",
      rankSub: "Médaille d'Argent",
      badgeText: "Olympiades Nationales",
      type: "silver",
      title: "2ème National MM-VET",
      desc: "2ème à l'Olympiade Nationale de la prestigieuse filière MM-VET au Bénin.",
      tag: "Compétition Nationale"
    },
    {
      rank: "2",
      rankSuffix: "ND",
      rankSub: "Vice-Champion",
      badgeText: "Camp d'Excellence",
      type: "bronze",
      title: "Vice-Champion National",
      desc: "Vice-champion au Camp d'Excellence des Chefs d'Établissement du Bénin.",
      tag: "Leadership Éducatif"
    }
  ];

  return (
    <section className="hero">
      {/* Section principale avec images en arrière-plan */}
      <div className="hero-main">
        <div className="hero-background">
          {images.map((image, index) => (
            <div
              key={index}
              className={`hero-background-image ${index === currentImageIndex ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                {toSentenceCase("Bienvenue au Groupe Scolaire et Universitaire")}
                <span className="highlight"> Les Palmiers</span>
              </h1>
              <h2 className="hero-subtitle">
                {toSentenceCase("De la Performance à la Référence")}
              </h2>
              <p className="hero-description">
                {toSentenceCase("Excellence académique, formation technique de pointe et accompagnement personnalisé pour construire votre avenir professionnel.")}
              </p>
              <div className="hero-buttons">
                <button 
                  className="btn btn-primary"
                  onClick={() => handleNavigation('inscription')}
                >
                  {toSentenceCase("S'inscrire maintenant")}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleNavigation('filieres')}
                >
                  {toSentenceCase("Découvrir nos filières")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sections sans images en arrière-plan */}
      <div className="hero-subsections">
        <div className="container">
          {/* Bannière Accès Rapide : Rentrée Scolaire, Fournitures & Horaires */}
          <div className="rentree-quick-banner" onClick={() => handleNavigation('documents-utiles')}>
            <div className="rentree-banner-content">
              <div className="rentree-banner-icon">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  <circle cx="12" cy="9" r="2"/>
                  <line x1="12" y1="11" x2="12" y2="14"/>
                </svg>
              </div>
              <div className="rentree-banner-text">
                <span className="rentree-banner-badge">Rentrée Scolaire 2026-2027</span>
                <h3>Listes des Fournitures & Emplois du Temps</h3>
                <p>Téléchargez librement les 29 listes officielles des manuels et les plannings de cours de la 6ème à la Terminale.</p>
              </div>
              <div className="rentree-banner-action">
                <button className="btn-rentree-download">
                  <span>Consulter & Télécharger</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Section Nos pôles */}
          <div className="poles-section">
            <div className="poles-header-premium">
              <span className="poles-header-badge">
                <IconShieldCheck />
                <span>Excellence Pédagogique & Diplômes d'État</span>
              </span>
              <h2 className="poles-main-title">
                Nos Pôles de <span className="highlight-orange">Formation</span>
              </h2>
              <div className="poles-title-divider"></div>
              <p className="poles-main-subtitle">
                Découvrez nos filières d'excellence, visualisez les aperçus interactifs et téléchargez les brochures officielles pour chaque cycle d'enseignement.
              </p>
            </div>
            
            <div className="poles-grid">
              {polesData.map((pole, idx) => (
                <div key={idx} className="pole-card">
                  <div className="pole-header-visual">
                    <div className={`pole-icon ${pole.iconClass}`}>
                      {pole.iconSvg}
                    </div>
                  </div>

                  <div className="pole-body">
                    <h3 className="pole-code">{pole.code}</h3>
                    <h4 className="pole-name">{toSentenceCase(pole.name)}</h4>
                    <p className="pole-desc">{toSentenceCase(pole.desc)}</p>
                  </div>

                  <div className="pole-card-actions">
                    <div className="pole-actions-row">
                      <button 
                        className="btn-pole-preview"
                        onClick={() => setSelectedBrochure(pole)}
                        title={`Voir l'aperçu officiel de la brochure ${pole.code}`}
                      >
                        <IconEye />
                        <span>Aperçu</span>
                      </button>
                      <button 
                        className="btn-pole-brochure"
                        onClick={() => handleDownloadBrochure(pole.pdfUrl, pole.pdfFilename)}
                        title={`Télécharger la brochure ${pole.code}`}
                      >
                        <IconDownload />
                        <span>Brochure</span>
                      </button>
                    </div>

                    <button 
                      className="btn-pole-inscrire"
                      onClick={() => handleNavigation('inscription')}
                      title={`S'inscrire à la formation ${pole.code}`}
                    >
                      <span>S'inscrire maintenant</span>
                      <IconArrowRight />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section Performance & Palmarès d'Excellence */}
          <div className="performance-section" aria-label="Distinctions et Palmarès">
            <div className="performance-container">
              {/* Header stylisé et prestigieux */}
              <div className="performance-header-premium">
                <span className="performance-header-badge">
                  <IconTrophy size={15} />
                  <span>Palmarès & Distinctions Nationales</span>
                </span>
                
                <h2 className="performance-main-title">
                  De la <span className="highlight-gold">Performance</span> à la <span className="highlight-cyan">Référence</span>
                </h2>
                
                <div className="performance-title-divider">
                  <span className="divider-line"></span>
                  <span className="divider-star"><IconSparkle size={12} /></span>
                  <span className="divider-line"></span>
                </div>
                
                <p className="performance-main-subtitle">
                  Le Groupe Scolaire & Universitaire Les Palmiers confirme son statut d'établissement d'élite à travers des résultats exceptionnels et la reconnaissance officielle de l'État.
                </p>
              </div>

              {/* Grille des cartes de distinction */}
              <div className="performance-grid">
                {performanceAchievements.map((item, index) => (
                  <div key={index} className={`performance-card-modern ${item.type}`}>
                    {/* Ligne d'accent lumineuse supérieure */}
                    <div className="card-top-glow"></div>
                    
                    {/* Badge catégorie / étiquette */}
                    <div className="performance-card-header">
                      <span className={`performance-category-badge badge-${item.type}`}>
                        {item.badgeText}
                      </span>
                    </div>

                    {/* Emblème Médaille 3D métallique */}
                    <div className="performance-emblem-wrap">
                      <div className={`medal-emblem-3d ${item.type}`}>
                        <div className="emblem-inner-glow"></div>
                        <div className="emblem-shine-sweep"></div>
                        
                        <div className="emblem-crown-icon">
                          {item.type === 'gold' && <IconCrown size={14} />}
                          {item.type === 'silver' && <IconAwardMedal size={14} />}
                          {item.type === 'bronze' && <IconTrophy size={14} />}
                        </div>

                        <div className="emblem-rank-digits">
                          <span className="emblem-num">{item.rank}</span>
                          <span className="emblem-suffix">{item.rankSuffix}</span>
                        </div>

                        <span className="emblem-sub-text">{item.rankSub}</span>
                      </div>
                    </div>

                    {/* Contenu textuel avec hiérarchie claire */}
                    <div className="performance-card-content">
                      <h3 className="performance-card-title">{item.title}</h3>
                      <p className="performance-card-desc">{item.desc}</p>
                    </div>

                    {/* Footer de la carte */}
                    <div className="performance-card-footer">
                      <span className="performance-tag-pill">
                        <span className="pill-dot"></span>
                        {item.tag}
                      </span>
                    </div>

                    {/* Filigrane d'arrière-plan décoratif */}
                    <div className="card-watermark-icon" aria-hidden="true">
                      <IconAwardMedal size={85} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* MODALE APERÇU BROCHURE OFFICIELLE */}
      {selectedBrochure && (
        <div className="modal-overlay" onClick={() => setSelectedBrochure(null)}>
          <div className="modal-container preview-modal-ultra" onClick={(e) => e.stopPropagation()}>
            
            {/* Header Stylisé */}
            <div className="preview-ultra-header">
              <div className="header-left-meta">
                <div className="header-tags">
                  <span className="ultra-tag-class">{selectedBrochure.code}</span>
                  <span className="ultra-tag-cycle">{selectedBrochure.name}</span>
                  <span className="ultra-tag-verified">
                    <IconShieldCheck />
                    <span>Brochure Officielle</span>
                  </span>
                </div>
                <h2 className="ultra-title">{selectedBrochure.code} - {selectedBrochure.name}</h2>
              </div>

              <button className="ultra-close-btn" onClick={() => setSelectedBrochure(null)} title="Fermer">
                <IconClose />
              </button>
            </div>

            {/* Toolbar */}
            <div className="preview-toolbar">
              <div className="toolbar-info">
                <span className="toolbar-dot"></span>
                <span>Document PDF Officiel Haute Définition • Année Académique 2026-2027</span>
              </div>
              <div className="toolbar-buttons">
                <a 
                  href={selectedBrochure.pdfUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  className="toolbar-btn"
                  title="Ouvrir dans un nouvel onglet"
                >
                  <IconExternalLink />
                  <span>Plein écran</span>
                </a>
                <button 
                  className="toolbar-btn" 
                  onClick={() => window.print()}
                  title="Imprimer directement"
                >
                  <IconPrinter />
                  <span>Imprimer</span>
                </button>
              </div>
            </div>

            {/* Corps du lecteur PDF */}
            <div className="preview-ultra-body">
              <div className="pdf-frame-container">
                <iframe 
                  src={`${selectedBrochure.pdfUrl}#view=FitH&toolbar=1`}
                  title={selectedBrochure.name}
                  className="pdf-iframe-view"
                />
              </div>
            </div>

            {/* Footer de la modale */}
            <div className="preview-ultra-footer">
              <div className="footer-meta">
                <span>Groupe Scolaire & Universitaire Les Palmiers • Cotonou</span>
              </div>
              <div className="footer-cta-group">
                <button className="btn btn-secondary" onClick={() => setSelectedBrochure(null)}>
                  Fermer
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleDownloadBrochure(selectedBrochure.pdfUrl, selectedBrochure.pdfFilename)}
                >
                  <IconDownload />
                  <span>Télécharger ({selectedBrochure.code})</span>
                </button>
                <button 
                  className="btn btn-primary btn-download-pulse" 
                  onClick={() => { setSelectedBrochure(null); handleNavigation('inscription'); }}
                >
                  <span>S'inscrire maintenant</span>
                  <IconArrowRight />
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </section>
  );
}

export default Hero;
